import type { SerializedStyles } from "@emotion/react";
import type { ChangeEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FloatingLabelInput } from "@aviary/components/FloatingLabelInput";
import {
  COUNTRIES,
  COUNTRY_CODE_TO_COUNTRY,
} from "@aviary/components/PhoneInput/CountrySelector/Countries";
import { CountrySelector } from "@aviary/components/PhoneInput/CountrySelector/CountrySelector";
import {
  getCleanNumber,
  isValidPhoneNumberPerCountry,
} from "@aviary/components/PhoneInput/validatePhoneNumber";
import { l } from "@aviary/locales/i18n";
import type { PopperPositionType } from "@aviary/types/popperPositions";

import * as styles from "./PhoneInput.styles";

const MIN_NUMBERS_MASK = "(###) ### #####";
const DEFAULT_COUNTRY_CODE = "US";

const getCountryFromCode = (countryCode: string) =>
  COUNTRIES.find(({ code }) => code === countryCode);

const formatWithDialCode = (dialCode: string, digits: string) =>
  digits === "" ? "" : `+${dialCode}${digits}`;

/**
 * @deprecated DS4 component — DS5 support is in progress. Do not add new usages.
 */
const removePlusSign = (phoneNumber: string) => phoneNumber.replace("+", "");

/**
 * @deprecated DS4 component — DS5 support is in progress. Do not add new usages.
 */
const getPhoneWithoutCountryCode = (value: string, preferredCountryCode = DEFAULT_COUNTRY_CODE) => {
  const cleanNumber = getCleanNumber(value);
  const country = getCountryFromNumber(value, preferredCountryCode);
  if (country) {
    const dialCodeRegExp = new RegExp(`^` + country.dialCode);

    return cleanNumber.replace(dialCodeRegExp, "");
  }

  return cleanNumber;
};

/**
 * @deprecated DS4 component — DS5 support is in progress. Do not add new usages.
 */
const getMinPhoneLength = (countryCode = DEFAULT_COUNTRY_CODE): number => {
  return COUNTRY_CODE_TO_COUNTRY[countryCode].minPhoneLength;
};

/**
 * @deprecated DS4 component — DS5 support is in progress. Do not add new usages.
 */
const getCountryFromNumber = (value: string, preferredCountryCode = DEFAULT_COUNTRY_CODE) => {
  const justDigits = getCleanNumber(value);
  if (justDigits === "" || !value.startsWith("+")) {
    return COUNTRIES.find(({ code }) => code === preferredCountryCode);
  }

  const countriesWithCode = COUNTRIES.filter(({ dialCode }) => justDigits.startsWith(dialCode));
  if (countriesWithCode.length > 1 && preferredCountryCode) {
    const preferredCountry = countriesWithCode.find(({ code }) => code === preferredCountryCode);
    if (preferredCountry) {
      return preferredCountry;
    }
  }

  return countriesWithCode?.length > 0
    ? countriesWithCode[0]
    : COUNTRIES.find(({ code }) => code === preferredCountryCode);
};

/**
 * @deprecated DS4 component — DS5 support is in progress. Do not add new usages.
 */
type PreferredCountries = "CA" | "US";

interface Props {
  /**
   * Unique field id
   */
  id: string;
  /**
   * Label for the phone input
   */
  label: string;
  /**
   * Input control value
   */
  value: string;
  /**
   * Callback from the HTML element on change
   */
  onChange: (formattedPhoneNumber: string) => void;
  /**
   * Input control name
   */
  name?: string;
  /**
   * Makes the field required
   *
   * @default false
   */
  required?: boolean;
  /**
   * Array of field-level errors
   */
  errors?: ReactNode[];
  /**
   * Text shown below the input to describe it's function
   */
  descriptiveText?: ReactNode;
  /**
   * Used when two countries have same number ("CA" or "US"), will update the flag icon
   * @default "US"
   */
  preferredCountryCode?: PreferredCountries;
  /**
   * Position direction that the content will appear
   * @default "bottom-start"
   */
  dropdownPlacement?: PopperPositionType;
  /**
   * Disables input
   * @default false
   */
  disabled?: boolean;
  /**
   * Callback when country is changed
   */
  onCountryChange?: (countryCode: string) => void;
  /**
   * Callback when phone digits are changed
   */
  onPhoneDigitsChange?: (phoneWithoutCountryCode: string) => void;
}

/**
 * @deprecated Use `PhoneInput` from `@fullscript/aviary-web` instead.
 */
const PhoneInput = (props: Props) => {
  const {
    id,
    label,
    value,
    onChange,
    errors,
    required,
    descriptiveText,
    preferredCountryCode = DEFAULT_COUNTRY_CODE,
    dropdownPlacement = "bottom-start",
    disabled = false,
    onCountryChange,
    onPhoneDigitsChange,
    ...rest
  } = props;

  const { t } = useTranslation();
  const [selectedCountry, setSelectedCode] = useState(
    getCountryFromNumber(value, preferredCountryCode)
  );

  const [isInputActive, setIsInputActive] = useState(false);

  const minPhoneLength = useMemo(
    () => getMinPhoneLength(selectedCountry?.code),
    [selectedCountry?.code]
  );

  const phoneInputWrapperStyles = [
    styles.phoneInputWrapper.base,
    disabled && styles.phoneInputWrapper.disabled,
  ];

  const phoneInputStyles = [styles.phoneInput, isInputActive && styles.focused];

  const phoneNumber = getPhoneWithoutCountryCode(value, selectedCountry?.code);
  const inputErrors = errors && errors.length > 0 ? errors : [];

  const enableActiveState = () => {
    if (disabled) return null;

    return setIsInputActive(true);
  };

  const disableActiveState = () => {
    if (disabled) return null;

    return setIsInputActive(false);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const cleanNum = getCleanNumber(e.target.value);
    onChange(formatWithDialCode(selectedCountry!.dialCode, cleanNum));

    if (onPhoneDigitsChange) {
      onPhoneDigitsChange(cleanNum);
    }
  };

  const handleSelectedCountry = (countryCode: string) => {
    const country = getCountryFromCode(countryCode);
    setSelectedCode(country);

    onChange(formatWithDialCode(country!.dialCode, phoneNumber));
    if (onCountryChange) {
      onCountryChange(countryCode);
    }
  };

  const hasInvalidPhoneNumber =
    !!value &&
    phoneNumber.length !== 0 &&
    !isValidPhoneNumberPerCountry(phoneNumber, minPhoneLength);

  const conditionalPaddingStyles = [
    styles.inputWrapperOverrides,
    selectedCountry?.dialCode?.length === 1 && styles.shortCode,
  ].filter(Boolean) as SerializedStyles[];

  return (
    <div css={phoneInputWrapperStyles} onBlur={() => disableActiveState()}>
      <div css={phoneInputStyles}>
        <div css={styles.countrySelector}>
          <CountrySelector
            selectedCode={selectedCountry?.code as string}
            onSelect={handleSelectedCountry}
            dropdownPlacement={dropdownPlacement}
            disabled={disabled}
            onFocus={() => enableActiveState()}
          />
        </div>
        <FloatingLabelInput
          id={id}
          label={label}
          type="tel"
          mask={MIN_NUMBERS_MASK}
          value={phoneNumber}
          handleChange={handleOnChange}
          errors={[...inputErrors, hasInvalidPhoneNumber && t(l.aviary.phoneInput.tooShort)]}
          inputMode="tel"
          required={required}
          descriptiveText={descriptiveText}
          inputStyles={conditionalPaddingStyles}
          disabled={disabled}
          autoComplete="tel"
          {...rest}
        />
      </div>
    </div>
  );
};

export {
  PhoneInput,
  removePlusSign,
  getPhoneWithoutCountryCode,
  getMinPhoneLength,
  getCountryFromNumber,
};

export type { PreferredCountries };
